import type { ImgHTMLAttributes } from 'react'
import config from 'src/config'
import { getTemplateImageProps } from 'src/functions/templateImage'

const sizes = '(max-width: 899px) calc((100vw - 76px) / 2), (max-width: 1120px) calc((100vw - 128px) / 4), 248px'
const widths = [128, 256, 384, 640]

/** Reuse the same prebuilt/cacheable variants as the landing page. The search
 * opens without fetching category records or downloading full-size originals. */
export const getSearchImageProps = (src: string): Pick<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet' | 'sizes'> => {
  const presentation = config.pageBuilderImagePresentation?.(src)
  if (presentation?.srcSet) return { src, srcSet: presentation.srcSet, sizes }

  const match = src.match(/^\/api\/images\/landingpages\/(\d+)\/([^/?#]+)\.[^/.?#]+$/)
  if (match) {
    const [, shop, basename] = match
    const variant = (width: number) => `/image/landingpages/${shop}/${basename}-w${width}.webp`
    return { src: variant(256), srcSet: widths.map(width => `${variant(width)} ${width}w`).join(', '), sizes }
  }

  return getTemplateImageProps(src, { fallbackWidth: 256, widths, sizes })
}

// Use the same responsive candidate as the eventual img so the browser can
// reuse its request. Only warm on search intent, never ahead of the page hero.
const warmed = new Set<string>()
export const warmSearchImages = (sources: string[]) => {
  if (typeof window === 'undefined') return
  for (const src of sources.slice(0, 12)) {
    if (warmed.has(src)) continue
    warmed.add(src)
    const props = getSearchImageProps(src)
    const image = new Image()
    image.decoding = 'async'
    image.onerror = () => warmed.delete(src)
    if (props.sizes) image.sizes = props.sizes
    if (props.srcSet) image.srcset = props.srcSet
    image.src = props.src || src
  }
}
